import React, { useState } from 'react';
import { useAppContext, useAppActions } from '../../context/AppContext';
import { 
  Heart, Plus, Search, Filter, Calendar, AlertTriangle, 
  CheckCircle, Clock, Eye, Edit3, UserCheck, MessageSquare,
  Phone, Mail, MapPin, Users, Shield, Bell, Star,
  Brain as BrainIcon, Cpu, Activity as ActivityIcon, TrendingUp as TrendingUpIcon,
  AlertCircle as AlertCircleIcon, Target, BarChart3 as BarChart3Icon
} from 'lucide-react';

const Family = ({ residentId }) => {
  const { state } = useAppContext();
  const actions = useAppActions();
  const { currentUser, deviceInfo } = state;
  const { optimization } = deviceInfo;

  const [selectedResident, setSelectedResident] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // AI Family Engagement Insights Data
  const aiFamilyData = {
    engagement: {
      overallScore: 87.3,
      communicationFrequency: 94.2,
      satisfaction: 4.6,
      participation: 78.9
    },
    predictions: {
      engagementRisk: 2,
      communicationGaps: 3,
      satisfactionDecline: 1,
      participationOpportunities: 5
    },
    recommendations: [
      {
        id: 'FAM_AI001',
        type: 'Engagement',
        priority: 'High',
        title: 'Increase communication frequency for Sarah Thompson',
        description: 'AI detected 40% decrease in communication frequency. Recommend proactive outreach.',
        impact: 'Improve engagement by 25%',
        family: 'Sarah Thompson',
        status: 'Pending',
        aiConfidence: 91
      },
      {
        id: 'FAM_AI002',
        type: 'Satisfaction',
        priority: 'Medium',
        title: 'Schedule family meeting for John Smith family',
        description: 'AI analysis shows declining satisfaction scores. Recommend family meeting.',
        impact: 'Improve satisfaction by 15%',
        family: 'John Smith Family',
        status: 'Recommended',
        aiConfidence: 84
      },
      {
        id: 'FAM_AI003',
        type: 'Participation',
        priority: 'Low',
        title: 'Invite family to participate in care planning',
        description: 'AI suggests involving family more in care planning decisions.',
        impact: 'Increase participation by 20%',
        family: 'All Families',
        status: 'Suggested',
        aiConfidence: 76
      }
    ],
    analytics: {
      engagementLevels: {
        high: 12,
        medium: 8,
        low: 3
      },
      communicationPatterns: {
        frequent: 15,
        moderate: 6,
        infrequent: 2
      },
      satisfactionTrends: {
        improving: 10,
        stable: 11,
        declining: 2
      }
    }
  };

  // Mock family data - in real app this would come from the resident's familyMembers array
  const familyData = [
    {
      id: 'FM001',
      residentId: 1,
      residentName: 'Eleanor Mary Thompson',
      name: 'Sarah Thompson',
      relationship: 'Daughter',
      phone: '0412 345 678',
      email: 'sarah.thompson@email.com',
      isPrimaryContact: true,
      notificationPreferences: ['medication_changes', 'health_updates', 'activities'],
      lastVisit: '2025-07-20',
      nextVisit: '2025-07-27',
      accessLevel: 'Full',
      status: 'Active',
      lastLogin: '2025-07-23 14:30',
      communicationHistory: [
        {
          id: 'COM001',
          date: '2025-07-23',
          type: 'message',
          content: 'How is mom doing today?',
          from: 'Sarah Thompson',
          to: 'Care Team',
          status: 'read'
        },
        {
          id: 'COM002',
          date: '2025-07-22',
          type: 'update',
          content: 'Blood sugar levels stable today',
          from: 'Care Team',
          to: 'Sarah Thompson',
          status: 'read'
        }
      ]
    },
    {
      id: 'FM002',
      residentId: 1,
      residentName: 'Eleanor Mary Thompson',
      name: 'Michael Thompson',
      relationship: 'Son',
      phone: '0423 456 789',
      email: 'michael.thompson@email.com',
      isPrimaryContact: false,
      notificationPreferences: ['emergencies', 'health_updates'],
      lastVisit: '2025-07-15',
      nextVisit: '2025-07-30',
      accessLevel: 'Limited',
      status: 'Active',
      lastLogin: '2025-07-20 10:15',
      communicationHistory: [
        {
          id: 'COM003',
          date: '2025-07-20',
          type: 'visit',
          content: 'Visited mom today, she seemed happy',
          from: 'Michael Thompson',
          to: 'Care Team',
          status: 'read'
        }
      ]
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Heart },
    { id: 'members', label: 'Family Members', icon: Users },
    { id: 'communication', label: 'Communication', icon: MessageSquare },
    { id: 'visits', label: 'Visits', icon: Calendar },
    { id: 'permissions', label: 'Permissions', icon: Shield },
    { id: 'ai', label: '🤖 AI Insights', icon: BrainIcon }
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Family Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">2</h3>
              <p className="text-sm text-gray-600">Family Members</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <MessageSquare className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">8</h3>
              <p className="text-sm text-gray-600">Messages This Week</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Calendar className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">3</h3>
              <p className="text-sm text-gray-600">Upcoming Visits</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Star className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">4.8</h3>
              <p className="text-sm text-gray-600">Satisfaction Score</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6">
          <h2 className={`${optimization.fontSize === 'text-sm' ? 'text-lg' : 'text-xl'} font-semibold mb-4`}>
            Recent Family Activity
          </h2>
          
          <div className="space-y-4">
            {familyData.map((family) => (
              <div key={family.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{family.name}</h3>
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        {family.relationship}
                      </span>
                      {family.isPrimaryContact && (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          Primary Contact
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-2">
                      Last visit: {family.lastVisit} | Next visit: {family.nextVisit}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {family.phone}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {family.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Last login: {family.lastLogin}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                      <MessageSquare className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg">
                      <Calendar className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg">
                      <Edit3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderFamilyMembers = () => (
    <div className="space-y-6">
      {/* Add Family Member Button */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <button className={`w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white ${optimization.buttonSize} rounded-xl font-semibold hover:opacity-90 ${optimization.animationSpeed} flex items-center justify-center gap-2`}>
          <Plus className={`${optimization.iconSize}`} />
          Add Family Member
        </button>
      </div>

      {/* Family Members List */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6">
          <h2 className={`${optimization.fontSize === 'text-sm' ? 'text-lg' : 'text-xl'} font-semibold mb-4`}>
            Family Members
          </h2>
          
          <div className="space-y-4">
            {familyData.map((family) => (
              <div key={family.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{family.name}</h3>
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        {family.relationship}
                      </span>
                      {family.isPrimaryContact && (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          Primary
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        family.accessLevel === 'Full' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {family.accessLevel} Access
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Contact Information</p>
                        <p className="text-sm">{family.phone}</p>
                        <p className="text-sm">{family.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Visit Schedule</p>
                        <p className="text-sm">Last: {family.lastVisit}</p>
                        <p className="text-sm">Next: {family.nextVisit}</p>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Notification Preferences</p>
                      <div className="flex flex-wrap gap-1">
                        {family.notificationPreferences.map((pref, index) => (
                          <span key={index} className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
                            {pref.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Send Message">
                      <MessageSquare className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg" title="Schedule Visit">
                      <Calendar className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg" title="Edit Permissions">
                      <Shield className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Remove">
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCommunication = () => (
    <div className="space-y-6">
      {/* Communication Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MessageSquare className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">24</h3>
              <p className="text-sm text-gray-600">Messages This Month</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">2.1 hrs</h3>
              <p className="text-sm text-gray-600">Avg Response Time</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Star className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">4.8</h3>
              <p className="text-sm text-gray-600">Satisfaction Rating</p>
            </div>
          </div>
        </div>
      </div>

      {/* Communication History */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6">
          <h2 className={`${optimization.fontSize === 'text-sm' ? 'text-lg' : 'text-xl'} font-semibold mb-4`}>
            Communication History
          </h2>
          
          <div className="space-y-4">
            {familyData.flatMap(family => family.communicationHistory).map((comm) => (
              <div key={comm.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{comm.from}</h3>
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {comm.type}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        comm.status === 'read' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {comm.status}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-2">{comm.content}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {comm.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <UserCheck className="w-4 h-4" />
                        To: {comm.to}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                      <MessageSquare className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg">
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderVisits = () => (
    <div className="space-y-6">
      {/* Visit Schedule */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className={`${optimization.fontSize === 'text-sm' ? 'text-lg' : 'text-xl'} font-semibold`}>
              Visit Schedule
            </h2>
            <button className={`bg-gradient-to-r from-purple-600 to-pink-600 text-white ${optimization.buttonSize} rounded-xl font-semibold hover:opacity-90 ${optimization.animationSpeed} flex items-center gap-2`}>
              <Plus className={`${optimization.iconSize}`} />
              Schedule Visit
            </button>
          </div>
          
          <div className="space-y-4">
            {familyData.map((family) => (
              <div key={family.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{family.name}</h3>
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        {family.relationship}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Last Visit</p>
                        <p className="text-sm font-medium">{family.lastVisit}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Next Visit</p>
                        <p className="text-sm font-medium">{family.nextVisit}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Reschedule">
                      <Calendar className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg" title="Confirm">
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAI = () => (
    <div className="space-y-6">
      {/* AI Family Engagement Dashboard */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-100 p-8 rounded-2xl border border-purple-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-purple-800 mb-2 flex items-center gap-3">
              <BrainIcon className="w-6 h-6 text-purple-600" />
              AI-Powered Family Engagement
            </h3>
            <p className="text-purple-600">Intelligent family communication and engagement optimization</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-green-600 font-medium">AI Active</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-green-600">{aiFamilyData.engagement.overallScore}%</div>
                <div className="text-sm text-gray-600">Engagement Score</div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Target</span>
                <span className="text-green-600">90%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full" style={{width: `${aiFamilyData.engagement.overallScore}%`}}></div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">{aiFamilyData.engagement.communicationFrequency}%</div>
                <div className="text-sm text-gray-600">Communication</div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Frequency</span>
                <span className="text-blue-600">High</span>
              </div>
              <div className="text-xs text-gray-500">Daily interactions</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
                <Star className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-purple-600">{aiFamilyData.engagement.satisfaction}/5</div>
                <div className="text-sm text-gray-600">Satisfaction</div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Rating</span>
                <span className="text-purple-600">Excellent</span>
              </div>
              <div className="text-xs text-gray-500">Family feedback</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-green-600">{aiFamilyData.engagement.participation}%</div>
                <div className="text-sm text-gray-600">Participation</div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Active</span>
                <span className="text-green-600">Good</span>
              </div>
              <div className="text-xs text-gray-500">Care involvement</div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Recommendations */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-gray-800">AI Family Engagement Recommendations</h4>
            <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
              {aiFamilyData.recommendations.length} Active
            </span>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {aiFamilyData.recommendations.map((recommendation) => (
            <div key={recommendation.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h5 className="font-semibold text-sm">{recommendation.title}</h5>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      recommendation.priority === 'High' ? 'bg-red-100 text-red-800' :
                      recommendation.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {recommendation.priority}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mb-2">{recommendation.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Target className="w-3 h-3" />
                      {recommendation.impact}
                    </span>
                    <span className="flex items-center gap-1">
                      <BrainIcon className="w-3 h-3" />
                      {recommendation.aiConfidence}% confidence
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {recommendation.family}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg">
                    <CheckCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Family Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h4 className="text-lg font-semibold text-gray-800">Engagement Levels</h4>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium">High Engagement</span>
              </div>
              <span className="text-sm text-green-600 font-medium">{aiFamilyData.analytics.engagementLevels.high} families</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-sm font-medium">Medium Engagement</span>
              </div>
              <span className="text-sm text-yellow-600 font-medium">{aiFamilyData.analytics.engagementLevels.medium} families</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-sm font-medium">Low Engagement</span>
              </div>
              <span className="text-sm text-red-600 font-medium">{aiFamilyData.analytics.engagementLevels.low} families</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h4 className="text-lg font-semibold text-gray-800">Communication Patterns</h4>
          </div>
          <div className="p-6 space-y-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{aiFamilyData.analytics.communicationPatterns.frequent}</div>
              <div className="text-sm text-gray-600 mb-1">Frequent</div>
              <div className="text-xs text-gray-500">Daily communication</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{aiFamilyData.analytics.communicationPatterns.moderate}</div>
              <div className="text-sm text-gray-600 mb-1">Moderate</div>
              <div className="text-xs text-gray-500">Weekly communication</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{aiFamilyData.analytics.communicationPatterns.infrequent}</div>
              <div className="text-sm text-gray-600 mb-1">Infrequent</div>
              <div className="text-xs text-gray-500">Monthly communication</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPermissions = () => (
    <div className="space-y-6">
      {/* Permission Settings */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6">
          <h2 className={`${optimization.fontSize === 'text-sm' ? 'text-lg' : 'text-xl'} font-semibold mb-4`}>
            Family Access Permissions
          </h2>
          
          <div className="space-y-4">
            {familyData.map((family) => (
              <div key={family.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{family.name}</h3>
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        {family.relationship}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        family.accessLevel === 'Full' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {family.accessLevel} Access
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Access Level</p>
                        <select 
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                          defaultValue={family.accessLevel}
                        >
                          <option value="Full">Full Access</option>
                          <option value="Limited">Limited Access</option>
                          <option value="Read Only">Read Only</option>
                        </select>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Notifications</p>
                        <div className="space-y-1">
                          {['medication_changes', 'health_updates', 'activities', 'emergencies'].map((pref) => (
                            <label key={pref} className="flex items-center gap-2 text-sm">
                              <input 
                                type="checkbox" 
                                defaultChecked={family.notificationPreferences.includes(pref)}
                                className="rounded"
                              />
                              {pref.replace('_', ' ')}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Save Changes">
                      <CheckCircle className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg" title="Advanced Settings">
                      <Shield className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'members':
        return renderFamilyMembers();
      case 'communication':
        return renderCommunication();
      case 'visits':
        return renderVisits();
      case 'permissions':
        return renderPermissions();
      case 'ai':
        return renderAI();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-pink-600 to-rose-600 rounded-2xl p-6 text-white shadow-xl min-h-[120px] flex items-center">
        <div className="flex justify-between items-center w-full">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              Family Management
              <Heart className="w-8 h-8 text-pink-300 animate-pulse" />
            </h1>
            <p className="text-base mt-2 opacity-90">
              Manage family connections, communication, and access for residents
            </p>
          </div>
          <div className="flex gap-2">
            <button className={`bg-white text-pink-600 ${optimization.buttonSize} rounded-xl font-semibold hover:bg-opacity-90 ${optimization.animationSpeed} flex items-center gap-2`}>
              <Plus className={`${optimization.iconSize}`} />
              Add Family
            </button>
            <button className={`bg-white text-pink-600 ${optimization.buttonSize} rounded-xl font-semibold hover:bg-opacity-90 ${optimization.animationSpeed} flex items-center gap-2`}>
              <MessageSquare className={`${optimization.iconSize}`} />
              Send Update
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-gray-200">
          <div className="flex space-x-1 p-4 overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all whitespace-nowrap ${
                    isActive ? 'bg-purple-100 text-purple-600' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className={optimization.iconSize} />
                  <span className={optimization.fontSize}>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Family;
